/**
 * @fileoverview What the plan allows of the Plant Doctor's checkup (allowed, on Free, or switched off by the owner), and the
 * door the plant card's Doctor tile makes of it (B138): one hook, usePlantDoctorDoor, for every state the tile can be in.
 *
 * usePlantDoctorAccess was moved word for word out of PlantDoctor (audit batch 18, A107). usePlantDoctorDoor replaces the
 * Checkup item of the card's old "..." menu (PlantDoctorMenuItem, deleted with the menu): advertised rather than hidden, a
 * gardener who cannot use it sees a lock. On Free or with AI switched off a tap still opens the Plant Doctor window, which says
 * why with its existing words (the Subscribe link on Free; the hello@ address when the owner switched it off); a paused plan's
 * tile goes to Subscribe; in someone else's garden a paused plan's tile is locked and only a toast says why, never a link to
 * Subscribe (useTendingFor; B23: the invitee only uses the inviter's plan). Without a photo a tap says to add one first (not
 * when AI is switched off, where the window says so).
 *
 * Where: PlantCard (the Doctor tile it gives CardTiles) and PlantDoctor (the window).
 * Settings: access.ai and access.aiRevoked (useAiAccess); the plan's state and whose garden it is (useBilling, useTendingFor).
 * Words (en/es): plants.diagnose.locked, needPhoto.
 *
 * @module usePlantDoctorAccess
 */

import { useAiAccess } from '@/features/billing/lib/useAiAccess';
import { useBilling, useTendingFor } from '@/features/billing/lib/useBilling';
import { useToast } from '@/shared/components/ui/use-toast';
import { useTranslation } from '@/shared/hooks/useTranslation';

/** What the plan allows of the checkup, for the Doctor tile and the window. */
export interface PlantDoctorAccess {
  /** The gardener may use check-ups (their AI is on and their plan has them). */
  allowed: boolean;
  /** On Free: the tile opens a window offering Grow More, not a check-up. */
  onFree: boolean;
  /** The owner switched this gardener's AI off. */
  revoked: boolean;
}

/**
 * What the plan allows of the checkup: allowed, on Free, or switched off by the owner, which no plan changes.
 *
 * @returns The three answers.
 */
export const usePlantDoctorAccess = (): PlantDoctorAccess => {
  const { allowed, revoked } = useAiAccess();
  const { data: billing } = useBilling();
  const onFree = !allowed && !revoked && billing?.access.onFree === true;
  return { allowed, onFree, revoked };
};

/** What the Doctor tile draws and does. */
export interface PlantDoctorDoor {
  /** The lock in place of the stethoscope (Free, AI switched off, or a paused plan). */
  locked: boolean;
  /** Greyed (aria-disabled) because there is no photo yet: a tap says to add one. */
  dim: boolean;
  /** A paused plan in the gardener's own garden: the tile is a link to this address; null otherwise. */
  to: string | null;
  /** The tile's tooltip. */
  title: string;
  /** A tap on the tile (not used when `to` is set). */
  press: () => void;
}

/**
 * The Doctor tile's door: its look and what a tap does, from the plan, whose garden it is and whether the plant has a photo.
 *
 * @param hasPhotos - Whether the plant has a photo; without one a tap says to add one.
 * @param onOpen - Opens the Plant Doctor window (the card's state).
 * @returns The tile's lock, dimming, link, tooltip and tap.
 */
export const usePlantDoctorDoor = (hasPhotos: boolean, onOpen: () => void): PlantDoctorDoor => {
  const { reactTranslate: t } = useTranslation();
  const { toast } = useToast();
  const { allowed, onFree, revoked } = usePlantDoctorAccess();
  /** Someone else's garden: the owner's plan, so no Subscribe (B23). */
  const tending = useTendingFor() !== null;
  const paused = !allowed && !onFree && !revoked;
  if (paused && tending) {
    return { locked: true, dim: false, to: null, title: t('plants.diagnose.locked'), press: () => toast({ description: t('plants.diagnose.locked') }) };
  }
  if (paused) {
    return { locked: true, dim: false, to: '/subscribe', title: t('plants.diagnose.locked'), press: () => undefined };
  }
  return {
    locked: onFree || revoked,
    dim: !hasPhotos && !revoked,
    to: null,
    title: hasPhotos ? t('plants.diagnose.ask') : t('plants.diagnose.needPhoto'),
    press: () => {
      if (!hasPhotos && !revoked) {
        toast({ description: t('plants.diagnose.needPhoto') });
        return;
      }
      onOpen();
    },
  };
};
